import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { createQuestion, updateQuestion } from '../../../services/firebase/forum';
import Button from '../../common/Button';
import UserMentionInput from '../../common/UserMentionInput';
import { toast } from 'react-hot-toast';


const QuestionForm = ({ 
  isOpen, 
  onClose, 
  onQuestionCreated,
  editingQuestion = null,
  mode = 'create' // 'create' or 'edit'
}) => {
  const { user, userProfile } = useAuthContext();
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Character limits
  const TITLE_MAX_LENGTH = 200;
  const CONTENT_MAX_LENGTH = 5000;


  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll on mobile
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    }

    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  // Initialize form data when editing
  useEffect(() => {
    if (mode === 'edit' && editingQuestion) {
      setFormData({
        title: editingQuestion.title || '',
        content: editingQuestion.content || ''
      });
    } else if (mode === 'create') {
      // Reset form for create mode
      setFormData({
        title: '',
        content: ''
      });
    }
  }, [mode, editingQuestion, isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormErrors({});

      if (mode === 'create') {
        setFormData({
          title: '',
          content: ''
        });
      }
    }
  }, [isOpen, mode]);

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field-specific errors when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    // Title validation
    if (!formData.title.trim()) {
      errors.title = 'Post title is required';
    } else if (formData.title.length > TITLE_MAX_LENGTH) {
      errors.title = `Title must be ${TITLE_MAX_LENGTH} characters or less`;
    }
    
    // Content validation - plain text validation
    if (!formData.content.trim()) {
      errors.content = 'Post content is required';
    } else if (formData.content.length > CONTENT_MAX_LENGTH) {
      errors.content = `Content must be ${CONTENT_MAX_LENGTH} characters or less`;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('You must be logged in to create a post');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (mode === 'create') {
        // Create new question
        const questionData = {
          title: formData.title.trim(),
          content: formData.content.trim(), // Plain text content
          tags: [], // No tags
          authorId: user.uid,
          authorName: user.displayName || user.email || 'Anonymous',
          authorPhotoURL: userProfile?.profilePicture || userProfile?.basicInfo?.photoURL || user.photoURL || ''
        };
        
        const result = await createQuestion(questionData);
        
        if (result.success) {
          toast.success('Post created successfully!');
          onQuestionCreated?.(result.data);
          onClose();
        } else {
          toast.error(result.error || 'Failed to create post');
          if (result.validationErrors) {
            const errors = {};
            result.validationErrors.forEach(error => {
              if (error.includes('title')) errors.title = error;
              if (error.includes('content')) errors.content = error;
            });
            setFormErrors(errors);
          }
        }
      } else {
        // Update existing question
        const updateData = {
          title: formData.title.trim(),
          content: formData.content.trim() // Plain text content
        };
        
        const result = await updateQuestion(editingQuestion.id, updateData, user.uid);
        
        if (result.success) {
          toast.success('Post updated successfully!');
          onQuestionCreated?.(result.data);
          onClose();
        } else {
          toast.error(result.error || 'Failed to update post');
          if (result.validationErrors) {
            const errors = {};
            result.validationErrors.forEach(error => {
              if (error.includes('title')) errors.title = error;
              if (error.includes('content')) errors.content = error;
            });
            setFormErrors(errors);
          }
        }
      }
    } catch (error) {
      console.error('Error submitting post:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get remaining character count
  const getRemainingChars = (text, maxLength) => {
    return maxLength - text.length;
  };

  // Get character count color
  const getCharCountColor = (remaining, maxLength) => {
    const percentage = remaining / maxLength;
    if (percentage < 0.1) return 'text-red-500';
    if (percentage < 0.2) return 'text-yellow-500';
    return 'text-gray-500';
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    {mode === 'edit' ? 'Edit Post' : 'Post'}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Title Field */}
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="What would you like to share?"
                      className={`block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 text-base sm:text-sm px-3 py-2 ${
                        formErrors.title ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                      }`}
                      maxLength={TITLE_MAX_LENGTH}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {formErrors.title && (
                        <p className="text-sm text-red-600">{formErrors.title}</p>
                      )}
                      <p className={`text-xs ml-auto ${getCharCountColor(getRemainingChars(formData.title, TITLE_MAX_LENGTH), TITLE_MAX_LENGTH)}`}>
                        {getRemainingChars(formData.title, TITLE_MAX_LENGTH)} characters remaining
                      </p>
                    </div>
                  </div>

                  {/* Content Field */}
                  <div>
                    <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Content *
                    </label>
                    <UserMentionInput
                      value={formData.content}
                      onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                      placeholder="Share your thoughts, ask for advice, or start a discussion. Use @ to mention other users!"
                      maxLength={CONTENT_MAX_LENGTH}
                      error={!!formErrors.content}
                      className="w-full"
                    />
                    {formErrors.content && (
                      <p className="text-sm text-red-600 mt-1">{formErrors.content}</p>
                    )}
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting || !formData.title.trim() || !formData.content.trim()}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {mode === 'edit' ? 'Updating...' : 'Posting...'}
                        </>
                      ) : (
                        mode === 'edit' ? 'Update Post' : 'Post'
                      )}
                    </Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default QuestionForm;